import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { conversationService } from '../services/conversationService';
import { useAuth } from './AuthContext';
import { getUserFacingError } from '../lib/userFacingErrors';

const ConversationContext = createContext(null);

export function ConversationProvider({ children, onThreadCreated }) {
  const { billingStatus, refreshBilling } = useAuth();
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadsError, setThreadsError] = useState(null);
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const [threadLoadError, setThreadLoadError] = useState(null);
  // messages in UI format: { id, role: 'user'|'assistant', text, createdAt, embeds?}
  const [messages, setMessages] = useState([]);
  // shared draft so QuickActions can prefill InputBar without a global state manager
  const [draftText, setDraftText] = useState('');
  // Overage warning from billing system (once per session)
  const [overageWarning, setOverageWarning] = useState(null);
  const [upgradePrompt, setUpgradePrompt] = useState(null);
  const overageDismissedRef = useRef(false);
  const loadRequestRef = useRef(0);

  // Helpers to transform
  const toApiHistory = (msgs) =>
    msgs.map((m) => ({
      role: m.role,
      content: m.text,
      embeds: m.embeds,
    }));
  const fromApiHistory = (history) =>
    history.map((m, idx) => ({
      id: `m-${idx}`,
      role: m.role,
      text: m.content || m.text,
      embeds: m.embeds,
      createdAt: m.createdAt || m.created_at || new Date().toISOString(),
    }));


  const loadThreads = async ({ silent = false } = {}) => {
    if (!silent) setThreadsLoading(true);
    if (!silent) setThreadsError(null);
    try {
      const list = await conversationService.listThreads();
      setThreads(list);
      if (!silent) setThreadsError(null);
      // Do NOT auto-select a thread on load; keep convo area empty until user action
    } catch (e) {
      // 402 = billing not set up yet, expected during onboarding
      if (!String(e).includes('402')) {
        console.error('Failed to load threads:', e);
        if (!silent) setThreadsError(getUserFacingError(e, 'chat-history'));
      }
      if (!silent) setThreads([]);
    } finally {
      if (!silent) setThreadsLoading(false);
    }
  };

  const loadThread = async (id) => {
    const requestId = loadRequestRef.current + 1;
    loadRequestRef.current = requestId;
    setCurrentThreadId(id);
    setThreadLoadError(null);
    try {
      const data = await conversationService.getThread(id);
      if (requestId !== loadRequestRef.current) return;
      setThreadLoadError(null);
      setMessages(
        (data.messages || []).map((m, idx) => ({
          id: m.id || `m-${idx}`,
          role: m.role,
          text: m.text,
          embeds: m.embeds,
          createdAt: m.createdAt || m.created_at || new Date().toISOString(),
        }))
      );
    } catch (e) {
      if (!String(e).includes('402')) {
        console.error('Failed to load thread:', e);
        setThreadLoadError(getUserFacingError(e, 'thread-load'));
      }
      if (requestId !== loadRequestRef.current) return;
      setMessages([]);
    }
  };

  const newConversation = () => {
    loadRequestRef.current += 1;
    // Reset the convo area; thread will be created upon first send
    setCurrentThreadId(null);
    setMessages([]);
    setDraftText('');
    setThreadLoadError(null);
  };


  const sendText = async (text) => sendTextStreaming(text);

  const sendTextStreaming = async (text) => {
    if (billingStatus?.billing_status === 'trialing' && billingStatus?.message_limit_reached) {
      setUpgradePrompt({
        reason: 'message_limit',
        message: 'Your free trial includes 25 messages. Upgrade to keep chatting.',
      });
      throw Object.assign(new Error('Free trial message limit reached'), { status: 402 });
    }

    // 1) Optimistic UI - add user message and empty assistant message
    const tempUserId = `temp-user-${Date.now()}`;
    const tempAssistantId = `temp-assistant-${Date.now() + 1}`;
    const nowIso = new Date().toISOString();

    const tempUserMsg = {
      id: tempUserId,
      role: 'user',
      text,
      embeds: [],
      createdAt: nowIso
    };

    const tempAssistantMsg = {
      id: tempAssistantId,
      role: 'assistant',
      text: '',
      embeds: [],
      createdAt: nowIso,
      isStreaming: true,  // Flag for UI indicators
      isLoading: true  // Loading state before first token
    };

    setMessages((prev) => [...prev, tempUserMsg, tempAssistantMsg]);

    // 2) Accumulators for streaming content
    let accumulatedText = '';
    const accumulatedEmbeds = [];

    try {
      await conversationService.streamTextInputResponse(
        text,
        currentThreadId,
        {
          // onTextDelta - accumulate and update UI
          onTextDelta: (deltaText) => {
            accumulatedText += deltaText;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === tempAssistantId
                  ? { ...m, text: accumulatedText, isLoading: false }
                  : m
              )
            );
          },

          // onEmbedComplete - append embed and update UI
          onEmbedComplete: (embed) => {
            accumulatedEmbeds.push(embed);
            setMessages((prev) =>
              prev.map((m) =>
                m.id === tempAssistantId
                  ? { ...m, embeds: [...accumulatedEmbeds] }
                  : m
              )
            );
          },

          // onDone - replace temp messages with real IDs
          onDone: (data) => {
            // Replace temp messages with real database messages
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id === tempUserId) {
                  return {
                    id: data.user_message.id,
                    role: 'user',
                    text: data.user_message.content,
                    embeds: data.user_message.embeds || [],
                    createdAt: data.user_message.created_at,
                  };
                }
                if (m.id === tempAssistantId) {
                  return {
                    id: data.assistant_message.id,
                    role: 'assistant',
                    text: data.assistant_message.content,
                    embeds: data.assistant_message.embeds || [],
                    createdAt: data.assistant_message.created_at,
                    isStreaming: false
                  };
                }
                return m;
              })
            );

            // Update thread_id after preserving the streamed messages in state.
            if (data.thread_id && data.thread_id !== currentThreadId) {
              setCurrentThreadId(data.thread_id);
              onThreadCreated?.(data.thread_id);
              loadThreads({ silent: true });
            }
            refreshBilling?.();
          },

          // onOverageWarning - show overage banner (once per session)
          onOverageWarning: (data) => {
            if (overageDismissedRef.current) return;
            setOverageWarning({
              used: data.used,
              quota: data.quota,
              overageCostCents: data.overage_cost_cents,
              periodEnd: data.period_end,
              planTier: data.plan_tier,
              nextTier: data.next_tier,
            });
          },

          // onError - show error message
          onError: (error) => {
            console.error('Streaming error:', error);
            const friendlyError = getUserFacingError(error, 'chat-stream');
            setMessages((prev) =>
              prev.map((m) =>
                m.id === tempAssistantId
                  ? { ...m, text: '', error: friendlyError, retryText: text, isStreaming: false, isLoading: false }
                  : m
              )
            );
          }
        }
      );
    } catch (error) {
      console.error('Stream failed:', error);
      if (error.status === 402 && error.detail?.code === 'free_trial_message_limit_reached') {
        setUpgradePrompt({
          reason: 'message_limit',
          message: error.detail.message || 'Your free trial includes 25 messages. Upgrade to keep chatting.',
        });
        refreshBilling?.();
        setMessages((prev) => prev.filter((m) => m.id !== tempUserId && m.id !== tempAssistantId));
        throw error;
      }
      const friendlyError = getUserFacingError(error, 'chat-stream');
      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempAssistantId
            ? { ...m, text: '', error: friendlyError, retryText: text, isStreaming: false, isLoading: false }
            : m
        )
      );
      throw error;
    }
  };

  
  const setFromApiHistory = (history) => setMessages(fromApiHistory(history));

  const value = useMemo(
    () => ({
      threads,
      threadsLoading,
      threadsError,
      currentThreadId,
      threadLoadError,
      messages,
      draftText,
      overageWarning,
      upgradePrompt,
      loadThreads,
      loadThread,
      newConversation,
      sendText,
      toApiHistory,
      setFromApiHistory,
      setMessages,
      setDraftText,
      dismissOverageWarning: () => {
        overageDismissedRef.current = true;
        setOverageWarning(null);
      },
      clearUpgradePrompt: () => setUpgradePrompt(null),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [threads, threadsLoading, threadsError, currentThreadId, threadLoadError, messages, draftText, overageWarning, upgradePrompt, billingStatus, refreshBilling]
  );

  useEffect(() => {
    // initial load
    loadThreads();
  }, []);

  return <ConversationContext.Provider value={value}>{children}</ConversationContext.Provider>;
}

export function useConversation() {
  const ctx = useContext(ConversationContext);
  if (!ctx) throw new Error('useConversation must be used within a ConversationProvider');
  return ctx;
}
